// Hook for cart price calculations
import { useCallback } from "react";
import type { EnhancedCartItem, CartCard, PriceBreakdown } from "../types";
import { SessionStorageManager } from "~/utils/sessionStorageManager";

export interface UseCartCalculationsReturn {
  resolveMenuItemBasePrice: (item: EnhancedCartItem, menuItems?: any[]) => number;
  resolveCardPricing: (item: EnhancedCartItem, card?: CartCard) => {
    unitBasePrice: number;
    unitAddOnTotal: number;
    quantity: number;
  };
  calculateItemTotal: (item: EnhancedCartItem, menuItems?: any[]) => number;
  calculateVariantsCost: (item: EnhancedCartItem) => number;
  getItemTotalBreakdown: (item: EnhancedCartItem, menuItems?: any[]) => PriceBreakdown;
  calculateTotal: (items: EnhancedCartItem[], menuItems?: any[]) => number;
  getTotalItemsCount: (items: EnhancedCartItem[]) => number;
}

export const useCartCalculations = (): UseCartCalculationsReturn => {
  // Resolve base price from menu item or fallback
  const resolveMenuItemBasePrice = useCallback(
    (item: EnhancedCartItem, menuItems?: any[]): number => {
      const menuItem = menuItems?.find((mi) => mi.id === item.itemId);
      const candidate = Number(menuItem?.price ?? item.basePrice ?? 0);
      return Number.isFinite(candidate) ? candidate : 0;
    },
    []
  );

  // Resolve card pricing with fallbacks
  const resolveCardPricing = useCallback(
    (item: EnhancedCartItem, card?: CartCard) => {
      const fallbackBase = Number(item.basePrice ?? 0);
      const summary = SessionStorageManager.calculateCardPricingSummary(
        card?.variants,
        fallbackBase
      );

      const unitBasePrice = Number.isFinite(card?.unitBasePrice)
        ? Number(card?.unitBasePrice)
        : summary.unitBasePrice;
      const unitAddOnTotal = Number.isFinite(card?.unitAddOnTotal)
        ? Number(card?.unitAddOnTotal)
        : summary.unitAddOnTotal;
      const quantity = Number.isFinite(card?.quantity)
        ? Math.max(0, Number(card?.quantity))
        : 0;

      return { unitBasePrice, unitAddOnTotal, quantity };
    },
    []
  );

  // Calculate variants cost for backward compatibility
  const calculateVariantsCost = useCallback(
    (item: EnhancedCartItem): number => {
      if (item.cards && item.cards.length > 0) {
        return item.cards.reduce((total, card) => {
          const { unitAddOnTotal, quantity } = resolveCardPricing(item, card);
          return total + unitAddOnTotal * quantity;
        }, 0);
      }

      if (item.variants && item.variants.length > 0) {
        return item.variants.reduce((total, variant) => {
          if (variant.isVariant) return total;

          if (
            (variant.variantType === "meals_and_combos" && variant.actionType === "upgrade") ||
            (variant.variantType === "variants_and_options" && variant.actionType === "upgrade")
          ) {
            return total;
          }

          return (
            total +
            variant.children.reduce((variantTotal, child) => {
              return variantTotal + Number(child.price || 0) * (variant.quantity || 1);
            }, 0)
          );
        }, 0);
      }

      return 0;
    },
    [resolveCardPricing]
  );

  // Calculate total for a single item
  const calculateItemTotal = useCallback(
    (item: EnhancedCartItem, menuItems?: any[]): number => {
      if (item.cards && item.cards.length > 0) {
        return item.cards.reduce((total, card) => {
          const { unitBasePrice, unitAddOnTotal, quantity } = resolveCardPricing(item, card);
          return total + (unitBasePrice + unitAddOnTotal) * quantity;
        }, 0);
      }

      const fallbackBase = resolveMenuItemBasePrice(item, menuItems);
      let baseTotal = fallbackBase * item.totalQuantity;
      let upgradeTotal = 0;

      if (item.variants && item.variants.length > 0) {
        item.variants.forEach((variant) => {
          const isUpgrade =
            (variant.variantType === "meals_and_combos" && variant.actionType === "upgrade") ||
            (variant.variantType === "variants_and_options" && variant.actionType === "upgrade");

          if (!isUpgrade || !variant.children || variant.children.length === 0) return;

          const upgradeQuantity = Math.max(variant.quantity || 1, 1);
          variant.children.forEach((child) => {
            if (child?.comboData?.combo_price) {
              upgradeTotal += Number(child.comboData.combo_price) * upgradeQuantity;
            } else {
              upgradeTotal += Number(child?.price || 0) * upgradeQuantity;
            }
          });
        });
      }

      if (upgradeTotal > 0) {
        baseTotal = upgradeTotal;
      }

      const selectionTotal = calculateVariantsCost(item);
      return baseTotal + selectionTotal;
    },
    [resolveCardPricing, resolveMenuItemBasePrice, calculateVariantsCost]
  );

  // Get detailed price breakdown for an item
  const getItemTotalBreakdown = useCallback(
    (item: EnhancedCartItem, menuItems?: any[]): PriceBreakdown => {
      let baseTotal = 0;
      let selectionTotal = 0;

      if (item.cards && item.cards.length > 0) {
        item.cards.forEach((card) => {
          const { unitBasePrice, unitAddOnTotal, quantity } = resolveCardPricing(item, card);
          baseTotal += unitBasePrice * quantity;
          selectionTotal += unitAddOnTotal * quantity;
        });
      } else {
        const fallbackBase = resolveMenuItemBasePrice(item, menuItems);
        baseTotal = fallbackBase * item.totalQuantity;

        let upgradeTotal = 0;
        if (item.variants && item.variants.length > 0) {
          item.variants.forEach((variant) => {
            const isUpgrade =
              (variant.variantType === "meals_and_combos" && variant.actionType === "upgrade") ||
              (variant.variantType === "variants_and_options" && variant.actionType === "upgrade");

            if (!isUpgrade || !variant.children || variant.children.length === 0) return;

            const upgradeQuantity = Math.max(variant.quantity || 1, 1);
            variant.children.forEach((child) => {
              if (child?.comboData?.combo_price) {
                upgradeTotal += Number(child.comboData.combo_price) * upgradeQuantity;
              } else {
                upgradeTotal += Number(child?.price || 0) * upgradeQuantity;
              }
            });
          });
        }

        if (upgradeTotal > 0) {
          baseTotal = upgradeTotal;
        }

        selectionTotal = calculateVariantsCost(item);
      }

      const grandTotal = baseTotal + selectionTotal;
      const safeQuantity = item.totalQuantity > 0 ? item.totalQuantity : 1;

      return {
        baseTotal,
        variantsTotal: selectionTotal,
        grandTotal,
        unitPrice: grandTotal / safeQuantity,
      };
    },
    [resolveCardPricing, resolveMenuItemBasePrice, calculateVariantsCost]
  );

  // Calculate total cart value
  const calculateTotal = useCallback(
    (items: EnhancedCartItem[], menuItems?: any[]): number => {
      return items.reduce((total, item) => {
        return total + calculateItemTotal(item, menuItems);
      }, 0);
    },
    [calculateItemTotal]
  );

  // Get total items count
  const getTotalItemsCount = useCallback((items: EnhancedCartItem[]): number => {
    return items.reduce((total, item) => {
      return total + item.totalQuantity;
    }, 0);
  }, []);

  return {
    resolveMenuItemBasePrice,
    resolveCardPricing,
    calculateItemTotal,
    calculateVariantsCost,
    getItemTotalBreakdown,
    calculateTotal,
    getTotalItemsCount,
  };
};
